import {
  getScanPostsOfType,
  isClavetedInlayCoreMaterial,
  type DeepPartial,
  type InlayCoreConfiguration,
  type ScanPostCatalog,
} from "@jasper/client-models";
import { Select, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ButtonSelect, ButtonToggle } from "../../components";
import type { FieldValueProps } from "../../types";
import { handleDivBlur } from "../../utils";

interface Props extends FieldValueProps<InlayCoreConfiguration> {
  itemMaterials: { value: string; label: string }[];
  scanPostCatalog: ScanPostCatalog;
}

const findScanPostType = (
  scanPost: string | undefined,
  { scanPosts }: ScanPostCatalog
) => scanPosts.find(({ value }) => value === scanPost)?.type ?? scanPost;

export const InlayCoreCharacteristicsForm = ({
  value,
  errors,
  onChange,
  itemMaterials,
  scanPostCatalog,
  onBlur,
}: Props) => {
  const { t } = useTranslation("forms", {
    keyPrefix: "forms.inlayCoreCharacteristics",
  });

  const hasClavette = value?.inlayCoreCharacteristics?.hasClavette;
  const scanPost = value?.inlayCoreCharacteristics?.scanPost;
  const scanPostType = findScanPostType(scanPost, scanPostCatalog);
  const scanPostsOfType = getScanPostsOfType(scanPostType, scanPostCatalog);

  const handleChange = (next: DeepPartial<InlayCoreConfiguration>) =>
    onChange(next);

  const keepsSelectedMaterial = (nextHasClavette?: boolean) =>
    !nextHasClavette ||
    isClavetedInlayCoreMaterial(value?.materialCharacteristics?.material);

  const handleClavetteChange = (nextHasClavette?: boolean) =>
    handleChange({
      ...value,
      inlayCoreCharacteristics: {
        ...value?.inlayCoreCharacteristics,
        hasClavette: nextHasClavette,
      },
      materialCharacteristics: keepsSelectedMaterial(nextHasClavette)
        ? value?.materialCharacteristics
        : undefined,
    });

  const handleScanPostChange = (nextScanPost?: string) =>
    handleChange({
      ...value,
      inlayCoreCharacteristics: {
        ...value?.inlayCoreCharacteristics,
        scanPost: nextScanPost,
      },
    });

  const availableItemMaterials = hasClavette
    ? itemMaterials.filter(({ value: materialId }) =>
        isClavetedInlayCoreMaterial(materialId)
      )
    : itemMaterials;

  const hasScanPostLeafChoice = scanPostsOfType.length > 0;
  const scanPostError = errors?.inlayCoreCharacteristics?.scanPost
    ? t("errors.scanPost")
    : undefined;

  return (
    <Stack w="100%" gap="md" onBlur={event => handleDivBlur(event, onBlur)}>
      <Stack gap="xs">
        <Text size="sm" fw="bold">
          {t("labels.hasClavette")}
        </Text>
        <ButtonToggle
          value={hasClavette}
          trueLabel={t("yes", { keyPrefix: "common" })}
          falseLabel={t("no", { keyPrefix: "common" })}
          onChange={handleClavetteChange}
          error={
            errors?.inlayCoreCharacteristics?.hasClavette
              ? t("errors.hasClavette")
              : undefined
          }
        />
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw="bold">
          {t("labels.scanPostType")}
        </Text>
        <ButtonSelect
          value={scanPostType}
          data={scanPostCatalog.types.map(
            ({ value: typeValue, label, description }) => ({
              value: typeValue,
              label,
              description,
            })
          )}
          onChange={handleScanPostChange}
          error={hasScanPostLeafChoice ? undefined : scanPostError}
        />
      </Stack>

      {hasScanPostLeafChoice && (
        <Select
          label={
            <Text size="sm" fw="bold">
              {t("labels.scanPost")}
            </Text>
          }
          placeholder={t("placeholders.scanPost")}
          searchable
          value={scanPost === scanPostType ? null : (scanPost ?? null)}
          data={scanPostsOfType.map(({ value: scanPostValue, label }) => ({
            value: scanPostValue,
            label,
          }))}
          error={scanPostError}
          onChange={nextScanPost =>
            handleScanPostChange(nextScanPost ?? undefined)
          }
        />
      )}

      <Select
        onBlur={onBlur}
        label={
          <Text size="sm" fw="bold">
            {t("labels.material")}
          </Text>
        }
        placeholder={t("placeholders.material")}
        description={
          hasClavette ? t("descriptions.clavetedMaterial") : undefined
        }
        value={value?.materialCharacteristics?.material ?? null}
        data={availableItemMaterials}
        error={
          errors?.materialCharacteristics?.material
            ? t("errors.material")
            : undefined
        }
        onChange={material =>
          handleChange({
            ...value,
            materialCharacteristics: { material: material ?? undefined },
          })
        }
      />
    </Stack>
  );
};
